import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as ItemComponentTypeActions from "../../../actions/itemComponentType";
import * as NavigationActions from "../../../actions/navigation";
import * as TrialGroupItemTemplateActions from "../../../actions/trialGroupItemTemplate";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import { CheckboxFormInput, NumberFormInput, SelectFormInput, TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { CollectionPage } from "../../../containers/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { TrialGroupItemTemplateBreadcrumbs, TrialGroupItemTemplateCreateBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { findLookup, generateOptionsFromLookup } from "../../../helpers/lookupHelper";
import { SettingsTrialSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { TrialLookupsLayout } from "../../index";

import "./TrialGroupItemTemplatePage.scss";

interface TrialGroupItemTemplatePageProps {
    form: Dtos.TrialGroupItemTemplate & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    trialGroupItemTemplate: Dtos.TrialGroupItemTemplate;
    collection: Dtos.Collection;
    lookups: Dtos.Lookup[];
    loadingTrialGroupItemTemplate: boolean;
    loadTrialGroupItemTemplateFailure: boolean;
    loadTrialGroupItemTemplateErrorCode: string;
    savingTrialGroupItemTemplate: boolean;
    saveTrialGroupItemTemplateSuccess: boolean;
    saveTrialGroupItemTemplateFailure: boolean;
    creatingTrialGroupItemTemplate: boolean;
    createTrialGroupItemTemplateSuccess: boolean;
    validatingTrialGroupItemTemplate: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    itemComponentTypes: Dtos.ItemComponentType[];
    loadingItemComponentTypes: boolean;
    loadItemComponentTypesSuccess: boolean;
    loadItemComponentTypesFailure: boolean;
    loadItemComponentTypesErrorCode: string;
    clearTrialGroupItemTemplate: TrialGroupItemTemplateActions.ITrialGroupItemTemplateClearActionCreator;
    loadTrialGroupItemTemplate: TrialGroupItemTemplateActions.ITrialGroupItemTemplateLoadByIdActionCreator;
    saveTrialGroupItemTemplate: TrialGroupItemTemplateActions.ITrialGroupItemTemplateSaveActionCreator;
    createTrialGroupItemTemplate: TrialGroupItemTemplateActions.ITrialGroupItemTemplateCreateActionCreator;
    validateTrialGroupItemTemplate: TrialGroupItemTemplateActions.ITrialGroupItemTemplateValidateActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeClearActionCreator;
    loadItemComponentTypes: ItemComponentTypeActions.IItemComponentTypeLoadActionCreator;
}

const reduxFormName: string = "reduxForms.trialGroupItemTemplate";

const TrialGroupItemTemplatePage: FC<TrialGroupItemTemplatePageProps> = ({
    form,
    formState,
    formProperties,
    trialGroupItemTemplate,
    collection,
    lookups,
    loadingTrialGroupItemTemplate,
    loadTrialGroupItemTemplateFailure,
    loadTrialGroupItemTemplateErrorCode,
    savingTrialGroupItemTemplate,
    saveTrialGroupItemTemplateSuccess,
    saveTrialGroupItemTemplateFailure,
    creatingTrialGroupItemTemplate,
    createTrialGroupItemTemplateSuccess,
    validatingTrialGroupItemTemplate,
    validationFailures,
    permissions,
    itemComponentTypes,
    loadingItemComponentTypes,
    // loadItemComponentTypesSuccess,
    loadItemComponentTypesFailure,
    loadItemComponentTypesErrorCode,
    clearTrialGroupItemTemplate,
    loadTrialGroupItemTemplate,
    saveTrialGroupItemTemplate,
    createTrialGroupItemTemplate,
    validateTrialGroupItemTemplate,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems,
    clearItemComponentTypes,
    loadItemComponentTypes
}) => {
    //
    // -- Params --
    const { groupId } = useParams<{groupId: string;}>();
    const groupIdNumber = parseInt(groupId, 10);
    const { trialGroupItemTemplateId } = useParams<{trialGroupItemTemplateId: string;}>();
    const trialGroupItemTemplateIdNumber = parseInt(trialGroupItemTemplateId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.TrialGroupItemTemplate = {} as Dtos.TrialGroupItemTemplate;

        if (!isNaN(groupIdNumber)) {
            extraData.groupId = groupIdNumber;
        }

        loadForm(reduxFormName, Object.assign({ ...trialGroupItemTemplate }, extraData));
    };


    // Load trial group item template
    useEffect(() => {
        if (
            trialGroupItemTemplateIdNumber &&
            !trialGroupItemTemplate &&
            !loadingTrialGroupItemTemplate
        ) {
            setDrawerItems(SettingsTrialSubMenu(permissions));
            setBreadcrumbs(
                TrialGroupItemTemplateBreadcrumbs(
                    trialGroupItemTemplateIdNumber,
                    isNaN(groupIdNumber) ? undefined : groupIdNumber
                )
            );
            loadTrialGroupItemTemplate(trialGroupItemTemplateIdNumber);
        } else if (
            !trialGroupItemTemplateIdNumber &&
            !creatingTrialGroupItemTemplate &&
            !createTrialGroupItemTemplateSuccess
        ) {
            createTrialGroupItemTemplate(isNaN(groupIdNumber) ? undefined : groupIdNumber);
            setBreadcrumbs(
                TrialGroupItemTemplateCreateBreadcrumbs(isNaN(groupIdNumber) ? undefined : groupIdNumber)
            );
            setDrawerItems([]);
        } else if (trialGroupItemTemplate && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }

        if (!itemComponentTypes && !loadingItemComponentTypes) {
            loadItemComponentTypes();
        }
    }, [
        trialGroupItemTemplateIdNumber,
        trialGroupItemTemplate,
        setDrawerItems,
        SettingsTrialSubMenu,
        permissions,
        setBreadcrumbs,
        TrialGroupItemTemplateBreadcrumbs,
        loadTrialGroupItemTemplate,
        createTrialGroupItemTemplate,
        initalised,
        setInitalised,
        loadReduxForm,
        loadingTrialGroupItemTemplate,
        creatingTrialGroupItemTemplate,
        createTrialGroupItemTemplateSuccess,
        TrialGroupItemTemplateCreateBreadcrumbs,
        itemComponentTypes,
        loadingItemComponentTypes,
        loadItemComponentTypes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialGroupItemTemplateFailure) {
            navigate(`/error/${loadTrialGroupItemTemplateErrorCode}`);
        }
        if (loadItemComponentTypesFailure) {
            navigate(`/error/${loadItemComponentTypesErrorCode}`);
        }
    }, [
        loadTrialGroupItemTemplateFailure,
        loadTrialGroupItemTemplateErrorCode,
        loadItemComponentTypesFailure,
        loadItemComponentTypesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (trialGroupItemTemplateIdNumber && trialGroupItemTemplate?.name) {
            setBreadcrumbs(
                TrialGroupItemTemplateBreadcrumbs(
                    trialGroupItemTemplateIdNumber,
                    isNaN(groupIdNumber) ? undefined : groupIdNumber,
                    trialGroupItemTemplate?.name
                )
            );
        }
    }, [
        trialGroupItemTemplateIdNumber,
        trialGroupItemTemplate,
        setBreadcrumbs,
        TrialGroupItemTemplateBreadcrumbs,
    ]);

    // Reload trial group item template after saving
    useEffect(() => {
        if (!savingTrialGroupItemTemplate && saveTrialGroupItemTemplateSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Trial group item template saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                let url: string = groupIdNumber
                    ? `/settings/trialgroup/itemtemplates/${groupIdNumber}`
                    : "/settings/trialgroup/itemtemplates";
                navigate(url);
            } else if (!trialGroupItemTemplateIdNumber) {
                navigate(
                    `/settings/trialgroup/itemtemplates/${trialGroupItemTemplate?.groupId}/template/${trialGroupItemTemplate?.id}`
                );
                setInitalised(false);
                clearTrialGroupItemTemplate();
            }
        }
    }, [
        savingTrialGroupItemTemplate,
        saveTrialGroupItemTemplateSuccess
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingTrialGroupItemTemplate && saveTrialGroupItemTemplateFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingTrialGroupItemTemplate, saveTrialGroupItemTemplateFailure]);

    // Cleanup effect to clear the trial group item template on component unmount
    useEffect(() => {
        return () => {
            clearTrialGroupItemTemplate();
            clearItemComponentTypes();
            resetForm(reduxFormName);
        };
    }, [
        clearTrialGroupItemTemplate,
        clearItemComponentTypes,
        resetForm,
        reduxFormName,
    ]);

    const onCancel = () => {
        let url: string = groupIdNumber
            ? `/settings/trialgroup/itemtemplates/${groupIdNumber}`
            : "/settings/trialgroup/itemtemplates";
        navigate(url);
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialGroupItemTemplate) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.TrialGroupItemTemplate = {} as Dtos.TrialGroupItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!data?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            await saveTrialGroupItemTemplate(
                Object.assign({ ...data }, changes),
                Object.assign({ ...collection }, collectionChanges)
            );

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.TrialGroupItemTemplate = {} as Dtos.TrialGroupItemTemplate;
            let collectionChanges: Dtos.Collection = {} as Dtos.Collection;

            if (!form?.allowIntraVersions) {
                changes.initialVersion = null;
            }

            if (form && hasBeenSubmitted && !validatingTrialGroupItemTemplate) {
                await validateTrialGroupItemTemplate(
                    Object.assign({ ...form }, changes),
                    Object.assign({ ...collection }, collectionChanges)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const renderCollection = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialGroupItemTemplatesUpdate,
                Dtos.Permission.TrialGroupItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        if (form && !loadingTrialGroupItemTemplate && itemComponentTypes) {
            return (
                <CollectionPage
                    itemComponentTypeId={
                        itemComponentTypes?.find((x) => x.value === "TrialGroup")?.id
                    }
                    collectionId={form.collectionId || 0}
                    readonly={formDisabled}
                />
            );
        }
    }, [
        form,
        permissions,
        savingTrialGroupItemTemplate,
        formState,
        loadingTrialGroupItemTemplate,
        itemComponentTypes,
    ]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.TrialGroupItemTemplatesUpdate,
                Dtos.Permission.TrialGroupItemTemplatesCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingTrialGroupItemTemplate ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            details: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <div className="card card-shadow">
                        <div className="form-row pt-4 pb-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        name: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        description: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextAreaFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                rows: 3,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        trialGroupItemTemplateCategoryId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "TrialGroupItemTemplate",
                                                            "TrialGroupItemTemplateCategoryId",
                                                            lookups
                                                        ),
                                                        form.trialGroupItemTemplateCategoryId
                                                    ),
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        rank: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: NumberFormInput,
                                            inputDisabled: formDisabled,
                                            groupClassName: "form-row bottom-0 border-top-0 pt-sm-0 pt-md-2 pt-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div className="col-12">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        groupId: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: SelectFormInput,
                                            inputDisabled: formDisabled || !isNaN(groupIdNumber),
                                            inputProps: {
                                                options:
                                                    generateOptionsFromLookup(
                                                        findLookup(
                                                            "TrialGroupItemTemplate",
                                                            "GroupId",
                                                            lookups
                                                        ),
                                                        form.groupId
                                                    ),
                                                clearable: true,
                                            },
                                            groupClassName: "form-row bottom-0 border-top-0 pb-sm-0 pb-md-2 pb-xl-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2 col-xl-1",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-11 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row py-2 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        allowIntraVersions: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow intra versions",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                            changeAction: (_model, value, _form, _parentModel) => {
                                                changeForm(`${reduxFormName}.allowIntraVersions`, value);
                                                if (!value) {
                                                    changeForm(`${reduxFormName}.initialVersion`, null);
                                                }
                                            },
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div className="col-lg-12 col-xl-6">
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        initialVersion: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: TextFormInput,
                                            inputDisabled: formDisabled || !form.allowIntraVersions,
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field mr-0 pr-0",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                        <div className="form-row pt-2 pb-4 px-4">
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        allowVersionChanges: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Allow version label changes",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pb-sm-0 pb-md-2 pb-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 col-xl-9 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                            <div
                                className="col-lg-12 col-xl-6"
                                style={{ display: "grid" }}
                            >
                                {renderFormControls(
                                    form,
                                    "trialGroupItemTemplate",
                                    {
                                        active: {
                                            type: RegistrationComponent.RegistrationControl,
                                            inputType: CheckboxFormInput,
                                            inputDisabled: formDisabled,
                                            inputProps: {
                                                label: "Active",
                                            },
                                            inputLabel: " ",
                                            groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0 pt-sm-0 pt-md-2 pt-xl-0 align-items-center",
                                            labelClassName: "col-form-label col-sm-12 col-md-2",
                                            fieldClassName: "col-sm-12 col-md-10 generic-form-field",
                                        },
                                    },
                                    lookups,
                                    validationFailures,
                                    formProperties,
                                    changeForm
                                )}
                            </div>
                        </div>
                    </div>
                ),
            },
            components: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Components</h1>
                        {renderCollection}
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Trial Group Item Template</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialGroupItemTemplate",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, false);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(`${reduxFormName}.saveAndReturn`, true);
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        form,
        permissions,
        savingTrialGroupItemTemplate,
        formState,
        lookups,
        generateOptionsFromLookup,
        findLookup,
        renderCollection,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        groupIdNumber,
    ]);

    return (
        <TrialLookupsLayout
            loading={
                loadingTrialGroupItemTemplate || creatingTrialGroupItemTemplate
            }
            permissions={permissions}
            permission={Dtos.Permission.TrialGroupItemTemplatesView}
        >
            {renderContent}
        </TrialLookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<TrialGroupItemTemplatePageProps> => {
    let trialGroupItemTemplate: Dtos.TrialGroupItemTemplate | undefined = 
        !(state.trialGroupItemTemplate.formData instanceof Array)
            ? state.trialGroupItemTemplate.formData
            : undefined;
    let collection: Dtos.Collection | undefined = 
        !(state.reduxForms.collection instanceof Array)
            ? state.reduxForms.collection
            : undefined;
    let permissions = state.user.userSummary ? state.user.userSummary.permissions : [];
    let itemComponentTypes = state.itemComponentTypes.formData instanceof Array
        ? state.itemComponentTypes.formData
        : undefined;

    return {
        trialGroupItemTemplate: trialGroupItemTemplate!,
        collection: collection!,
        loadingTrialGroupItemTemplate:
            state.trialGroupItemTemplate.loadState &&
            state.trialGroupItemTemplate.loadState.status == RequestState.Pending,
        loadTrialGroupItemTemplateFailure:
            state.trialGroupItemTemplate.loadState &&
            state.trialGroupItemTemplate.loadState.status == RequestState.Failure,
        loadTrialGroupItemTemplateErrorCode: state.trialGroupItemTemplate.loadState.errorCode,
        savingTrialGroupItemTemplate:
            state.trialGroupItemTemplate.saveState &&
            state.trialGroupItemTemplate.saveState.status == RequestState.Pending,
        saveTrialGroupItemTemplateSuccess:
            state.trialGroupItemTemplate.saveState &&
            state.trialGroupItemTemplate.saveState.status == RequestState.Success,
        saveTrialGroupItemTemplateFailure:
            state.trialGroupItemTemplate.saveState &&
            state.trialGroupItemTemplate.saveState.status == RequestState.Failure,
        creatingTrialGroupItemTemplate:
            state.trialGroupItemTemplate.createState &&
            state.trialGroupItemTemplate.createState.status == RequestState.Pending,
        createTrialGroupItemTemplateSuccess:
            state.trialGroupItemTemplate.createState &&
            state.trialGroupItemTemplate.createState.status == RequestState.Success,
        validatingTrialGroupItemTemplate:
            state.trialGroupItemTemplate.validateState &&
            state.trialGroupItemTemplate.validateState.status == RequestState.Pending,
        permissions: permissions,
        lookups: state.trialGroupItemTemplate.lookups,
        form: state.reduxForms.trialGroupItemTemplate,
        formState: state.trialGroupItemTemplate.formState,
        formProperties: state.trialGroupItemTemplate.formProperties,
        validationFailures: trialGroupItemTemplate
            ? state.trialGroupItemTemplate.validationFailures
            : undefined,
        itemComponentTypes: itemComponentTypes,
        loadingItemComponentTypes:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Pending,
        loadItemComponentTypesSuccess:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Success,
        loadItemComponentTypesFailure:
            state.itemComponentTypes.loadState &&
            state.itemComponentTypes.loadState.status == RequestState.Failure,
        loadItemComponentTypesErrorCode: state.itemComponentTypes.loadState.errorCode,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialGroupItemTemplatePageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadTrialGroupItemTemplate: bindActionCreators(
            TrialGroupItemTemplateActions.LoadTrialGroupItemTemplateById, 
            dispatch
        ),
        clearTrialGroupItemTemplate: bindActionCreators(
            TrialGroupItemTemplateActions.Clear,
            dispatch
        ),
        saveTrialGroupItemTemplate: bindActionCreators(
            TrialGroupItemTemplateActions.SaveTrialGroupItemTemplate,
            dispatch
        ),
        createTrialGroupItemTemplate: bindActionCreators(
            TrialGroupItemTemplateActions.CreateTrialGroupItemTemplate,
            dispatch
        ),
        validateTrialGroupItemTemplate: bindActionCreators(
            TrialGroupItemTemplateActions.ValidateTrialGroupItemTemplate,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
        loadItemComponentTypes: bindActionCreators(ItemComponentTypeActions.LoadItemComponentType, dispatch),
        clearItemComponentTypes: bindActionCreators(ItemComponentTypeActions.Clear, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(TrialGroupItemTemplatePage);
